import type {
  AnalitoParaEscolhaResponse,
  ExameResponse,
  ExtracaoDeExameResponse,
  RegistrarExameExternoDtoResultadosItem,
  RegistrarExameExternoDtoResultadosSemAnalitoItem,
} from '@/api/generated/model';
import { achatar } from '@/shared/lib/texto';

/**
 * OS VALORES DE UM LAUDO EXTERNO, do jeito que a tela os edita.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Uma linha é UM valor do papel. Ela pode estar ligada a um analito do
 *  catálogo (`analitoId`) ou não — e é só isso que decide o destino dela:
 *
 *    · com analito  → vira resultado do exame e ENTRA no gráfico de evolução;
 *    · sem analito  → fica guardada no laudo, como texto, e não entra em
 *                     gráfico nenhum (não existe série onde o ponto cairia).
 *
 *  Tudo aqui é função pura: o que a leitura automática propõe, o que a pessoa
 *  digita e o que vai para o servidor são a mesma lista, e as três conversões
 *  precisam ser testáveis sem montar o diálogo.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface ValorDoLaudo {
  /** Chave de lista do React — a linha não tem id antes de ser salva. */
  chave: string;
  /** O nome como está impresso no laudo. */
  nomeNoLaudo: string;
  /** `''` = sem analito: fora do gráfico. */
  analitoId: string;
  /** Guardado junto do id para o seletor mostrar o escolhido sem buscar de novo. */
  analitoNome: string;
  analitoExame: string;
  /** A unidade do analito no CATÁLOGO — é contra ela que a do laudo é comparada. */
  analitoUnidade: string;
  /** Um campo só: "3,25" vira número, "< 0,5" e "Negativo" vão como texto. */
  valor: string;
  unidade: string;
  /** A faixa de referência impressa no laudo, como texto. */
  referencia: string;
  /** O que o servidor achou parecido — oferecido primeiro no seletor. */
  candidatos: AnalitoParaEscolhaResponse[];
}

let contador = 0;
const novaChave = (): string => `valor-${++contador}`;

export function linhaVazia(): ValorDoLaudo {
  return {
    chave: novaChave(),
    nomeNoLaudo: '',
    analitoId: '',
    analitoNome: '',
    analitoExame: '',
    analitoUnidade: '',
    valor: '',
    unidade: '',
    referencia: '',
    candidatos: [],
  };
}

/** Liga (ou desliga, com `null`) a linha a um analito. */
export function comAnalito(
  linha: ValorDoLaudo,
  analito: AnalitoParaEscolhaResponse | null,
): ValorDoLaudo {
  return {
    ...linha,
    analitoId: analito?.id ?? '',
    analitoNome: analito?.nome ?? '',
    analitoExame: analito?.exameNome ?? '',
    analitoUnidade: analito?.unidade ?? '',
  };
}

/** "3,25" → 3.25. `null` quando o texto não é um número puro ("< 0,5"). */
export function lerNumero(texto: string): number | null {
  const limpo = texto.trim();
  if (!/^-?\d+([.,]\d+)?$/.test(limpo)) return null;
  return Number(limpo.replace(',', '.'));
}

/** Número de volta para o campo, com vírgula — é como a pessoa lê no laudo. */
function escreverNumero(valor: number): string {
  return String(valor).replace('.', ',');
}

/**
 * "µg/dL", "μg/dl", "ug/dL" e "mcg/dL" são a mesma unidade. Espelha
 * `normalizarUnidade` do backend (`casamento-externo.ts`) — o alerta da tela
 * precisa acender nos mesmos casos em que o do servidor acenderia.
 */
function normalizarUnidade(valor: string): string {
  return valor.trim().toLowerCase().replace(/[µμ]/g, 'u').replace(/mcg/g, 'ug').replace(/\s+/g, '');
}

/** O laudo traz uma unidade e o analito escolhido, outra. */
export function unidadeDiverge(linha: ValorDoLaudo): boolean {
  if (!linha.analitoId || !linha.unidade.trim() || !linha.analitoUnidade.trim()) return false;
  return normalizarUnidade(linha.unidade) !== normalizarUnidade(linha.analitoUnidade);
}

/** As linhas que a leitura automática propôs, prontas para a tela. */
export function linhasDaLeitura(leitura: ExtracaoDeExameResponse): ValorDoLaudo[] {
  return leitura.resultados.map((resultado) =>
    comAnalito(
      {
        ...linhaVazia(),
        nomeNoLaudo: resultado.nomeNoLaudo,
        valor:
          resultado.valorNumerico !== null
            ? escreverNumero(resultado.valorNumerico)
            : (resultado.valorTexto ?? ''),
        unidade: resultado.unidade ?? '',
        referencia: resultado.referenciaNoLaudo ?? '',
        candidatos: resultado.candidatos,
      },
      resultado.analito,
    ),
  );
}

/** As linhas de um exame já salvo, para o diálogo de edição. */
export function linhasDoExame(exame: ExameResponse): ValorDoLaudo[] {
  const ligadas = exame.resultados.map((resultado) => ({
    ...linhaVazia(),
    nomeNoLaudo: resultado.analitoNome,
    analitoId: resultado.analitoId,
    analitoNome: resultado.analitoNome,
    valor:
      resultado.valorNumerico !== null
        ? escreverNumero(resultado.valorNumerico)
        : (resultado.valorTexto ?? ''),
    unidade: resultado.unidade ?? '',
    referencia: referenciaDaObservacao(resultado.observacao),
  }));

  const soltas = exame.resultadosSemAnalito.map((resultado) => ({
    ...linhaVazia(),
    nomeNoLaudo: resultado.nome,
    valor: resultado.valor,
    unidade: resultado.unidade ?? '',
    referencia: resultado.referencia ?? '',
  }));

  return [...ligadas, ...soltas];
}

/**
 * Junta o que uma leitura trouxe ao que já está na tela.
 *
 * Existe porque um exame tem vários documentos — as três fotos das páginas do
 * mesmo laudo — e cada um é lido à parte. A regra é não perder nada do que já
 * está lá e não duplicar o que chega de novo:
 *
 *  · o mesmo valor de novo (mesmo nome e mesmo valor) é descartado — é a
 *    segunda via, ou o "Ler de novo";
 *  · valor NOVO para um analito que já tem linha entra SEM analito, com ele
 *    entre os candidatos: dois valores não cabem no mesmo analito do mesmo
 *    exame, e escolher qual fica é da pessoa, não desta função.
 */
export function mesclarLinhas(
  atuais: readonly ValorDoLaudo[],
  novas: readonly ValorDoLaudo[],
): ValorDoLaudo[] {
  const resultado = [...atuais];
  const assinatura = (linha: ValorDoLaudo) =>
    `${achatar(linha.nomeNoLaudo.trim())}|${linha.valor.trim()}`;

  for (const nova of novas) {
    if (resultado.some((linha) => assinatura(linha) === assinatura(nova))) continue;

    const analitoOcupado =
      nova.analitoId !== '' && resultado.some((linha) => linha.analitoId === nova.analitoId);
    resultado.push(analitoOcupado ? comAnalito(nova, null) : nova);
  }

  return resultado;
}

/** Mensagem de erro por chave de linha. Vazio = pode enviar. */
export type ErrosDosValores = Record<string, string>;

/**
 * O que impede o envio. Linha inteiramente vazia não é erro — é ignorada, como
 * a linha em branco que sobrou depois de "Adicionar valor".
 */
export function validarValores(linhas: readonly ValorDoLaudo[]): ErrosDosValores {
  const erros: ErrosDosValores = {};
  const vistos = new Map<string, string>();

  for (const linha of linhas) {
    if (estaVazia(linha)) continue;

    if (!linha.valor.trim()) {
      erros[linha.chave] = 'Informe o valor, ou remova a linha.';
      continue;
    }
    if (!linha.analitoId && !linha.nomeNoLaudo.trim()) {
      erros[linha.chave] = 'Informe o nome do exame, ou escolha um analito.';
      continue;
    }
    if (linha.analitoId) {
      if (vistos.has(linha.analitoId)) {
        erros[linha.chave] =
          'Este analito já tem um valor neste exame. Escolha outro, ou deixe sem analito.';
        continue;
      }
      vistos.set(linha.analitoId, linha.chave);
    }
  }

  return erros;
}

function estaVazia(linha: ValorDoLaudo): boolean {
  return (
    !linha.nomeNoLaudo.trim() && !linha.analitoId && !linha.valor.trim() && !linha.unidade.trim()
  );
}

const PREFIXO_DA_REFERENCIA = 'Referência no laudo: ';

function referenciaDaObservacao(observacao: string | null): string {
  return observacao?.startsWith(PREFIXO_DA_REFERENCIA)
    ? observacao.slice(PREFIXO_DA_REFERENCIA.length)
    : '';
}

/**
 * O que vai para o servidor: as linhas com analito de um lado, as sem do outro.
 *
 * ⚠️ A faixa impressa no laudo viaja na `observacao` do resultado. A
 * interpretação (alto/baixo) é calculada pela faixa do NOSSO catálogo — é o
 * que mantém a mesma régua ao longo do gráfico —, mas a do laboratório de fora
 * é dado clínico e não pode se perder só porque não é ela que colore o ponto.
 */
export function valoresParaEnvio(linhas: readonly ValorDoLaudo[]): {
  resultados: RegistrarExameExternoDtoResultadosItem[];
  resultadosSemAnalito: RegistrarExameExternoDtoResultadosSemAnalitoItem[];
} {
  const resultados: RegistrarExameExternoDtoResultadosItem[] = [];
  const resultadosSemAnalito: RegistrarExameExternoDtoResultadosSemAnalitoItem[] = [];

  for (const linha of linhas) {
    if (estaVazia(linha)) continue;

    const valor = linha.valor.trim();
    const unidade = linha.unidade.trim() || null;
    const referencia = linha.referencia.trim() || null;

    if (linha.analitoId) {
      const numero = lerNumero(valor);
      resultados.push({
        analitoId: linha.analitoId,
        valorNumerico: numero,
        valorTexto: numero === null ? valor : null,
        unidade,
        observacao: referencia ? `${PREFIXO_DA_REFERENCIA}${referencia}`.slice(0, 500) : null,
      });
    } else {
      resultadosSemAnalito.push({ nome: linha.nomeNoLaudo.trim(), valor, unidade, referencia });
    }
  }

  return { resultados, resultadosSemAnalito };
}
